import { Box } from '@mui/material';
import { btnSize, btnType } from '../../../../packages/ui/atoms/button';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Header from '../../../../components/v2/Header';
import Typography from '../../../../components/v2/Typography';
import { typography } from '../../../../components/v2/Typography/index.constant';

import SubmitDrawer from '../caqh-information/components/drawers/SubmitDrawer';
import ImportDrawer from '../caqh-information/components/drawers/ImportDrawer';

import { useStyles } from './index.styles';
import PriorityEmpty from '../../member-detail/components/PriorityEmpty';
import { SectionNames } from '../caqh-information/index.constants';
import { CardStatus, CardType, ProfileSectionCard } from '../common/card/profile-section-card';
import { getCaqhData, transformData } from '../common/utils.constants';
import { InfoCard } from '../common/card/info-card';
import { putProviderData } from '../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';

const Licenses = ({ refresh, userId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitList, setSubmitList] = useState({ filled: [], missing: [], list: [], title: undefined });
  const [professionallicenses, setProfessionalLicenses] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchCaqhData = async () => {
    const caqhData = await getCaqhData({ providerId: userId, type: 'professional-licenses' });
    if (caqhData) {
      setProfessionalLicenses(caqhData);
    }
  };

  useEffect(() => {
    void fetchCaqhData();
  }, []);

  const onApprove = async id => {
    const type = 'professional-licenses';
    const requestBody = {
      providerId: userId,
      data: {
        id,
        documentStatus: 'APPROVED',
      },
    };
    try {
      const response = await putProviderData(type, requestBody);
      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'License approved',
          }),
        );
        void fetchCaqhData();
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }
  };

  const onUpdate = id => {
    const data = professionallicenses.find(i => i._id === id);
    const list = transformData(data?.mainData, CardType.LICENSES);
    const filesData = transformData(data?.filesData, CardType.LICENSES);
    setShowSubmitModal(true);
    setSubmitList({
      filled: list?.filled || [],
      missing: list?.missing || [],
      list: list?.list || [],
      title: 'Licenses',
    });
    setSelectedRecord(data);
    setEditMode(true);
    setShowSubmitModal(true);
  };

  const onFormSubmit = async (data, edit) => {
    const type = 'professional-licenses';
    let fileData = {};
    if (data.fileData) {
      fileData = data.fileData;
    }
    const requestBody = {
      providerId: userId,
      data: {
        id: selectedRecord._id,
        state: data.state,
        licenseNo: data.licenseNo,
        type: data.type,
        issueDate: data.issueDate,
        expiryDate: data.expiryDate,
        ...fileData,
      },
    };
    try {
      const response = await putProviderData(type, requestBody);
      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'License data updated',
          }),
        );
      } else {
        setSubmitList({ filled: [], missing: [], list: [], title: undefined });
        void fetchCaqhData();
      }
    } catch (e) {
      console.warn(e);
      if (e.data) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.data.error,
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.message || 'Something went wrong',
          }),
        );
      }
    }
    onCloseModal();
    void fetchCaqhData();
  };

  const resetInformation = () => {
    setSubmitList({ filled: [], missing: [], list: [], title: undefined });
  };

  const onCloseModal = () => {
    setSelectedRecord(null);
    setShowNewUpload(false);
    setShowSubmitModal(false);
    refresh();
    void fetchCaqhData();
    resetInformation();
  };

  return (
    <>
      <Header
        label="Licenses"
        actions={[
          {
            children: <Typography {...typography.body.normal.medium.bold}>Add new</Typography>,
            icon: 'plus',
            variant: btnType.PRIMARY,
            size: btnSize.SMALL,
            onClick: () => setShowNewUpload(true),
            className: classes.primaryBtn,
          },
        ]}
      />
      <InfoCard records={professionallicenses} />
      {professionallicenses?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {professionallicenses?.map((license, index) => {
            const cardData =
              license?.documentStatus === CardStatus.DISCREPANCY ? license?.filesData : license?.mainData;
            const discrepancies = [];
            if (license?.documentStatus === CardStatus.DISCREPANCY) {
              license?.issues?.includes('type') && discrepancies.push('name');
              license?.issues?.includes('licenseNo') && discrepancies.push('number');
              license?.issues?.includes('state') && discrepancies.push('states');
              license?.issues?.includes('issueDate') && discrepancies.push('issueDate');
              license?.issues?.includes('expiryDate') && discrepancies.push('expiryDate');
            }
            return (
              <ProfileSectionCard
                key={index}
                data={license}
                status={license?.documentStatus || 'INCOMPLETE'}
                name={cardData?.type || ''}
                number={cardData?.licenseNo || ''}
                states={[cardData?.state || '']}
                issueDate={cardData?.issueDate || ''}
                expiryDate={cardData?.expiryDate || ''}
                file={license?.files?.[0] || 'Missing'}
                type={CardType.LICENSES}
                onUpdate={() => onUpdate(license?._id)}
                onApprove={() => onApprove(license?._id)}
                discrepancies={discrepancies}
              />
            );
          })}
        </Box>
      ) : (
        <PriorityEmpty
          title="Licenses will appear here once added."
          description=""
          glyph="empty-licenses"
          glyphOrientation="portrait"
          isEmptyWithLottie
          lottieSize={128}
        />
      )}
      {showNewUpload && (
        <ImportDrawer
          open={showNewUpload}
          onClose={onCloseModal}
          type={SectionNames.LICENSES}
          userId={userId}
        />
      )}
      {showSubmitModal && (
        <SubmitDrawer
          missedKeys={submitList.missing || []}
          filledKeys={submitList.filled}
          open={showSubmitModal}
          title="Licenses"
          subTitle="Licenses description"
          list={submitList.list}
          onClose={onCloseModal}
          userId={userId}
          onEdit={() => {
            setEditMode(true);
          }}
          isTabView
          editMode={editMode}
          onFormSubmit={onFormSubmit}
          selectedRecord={selectedRecord}
          fileType={SectionNames.LICENSES}
        />
      )}
    </>
  );
};

export default Licenses;
